import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Swords, Loader2, Users, Package, Gem, TrendingUp, TrendingDown, Dices, HelpCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { motion } from 'motion/react'
import useBattleStore from '../store/useBattleStore'
import useAppStore from '../../../store/useAppStore'
import AnimatedButton from '../../../components/AnimatedButton'
import HelpModal from '../../../components/HelpModal'
import useAudioManager from '../../../hooks/useAudioManager'
import toast from 'react-hot-toast'

const MAX_VISIBLE_CATEGORIES = 6

const getCaseCategories = (caseItem) => {
  if (!Array.isArray(caseItem.category)) {
    return []
  }

  return caseItem.category.filter(
    (category) => typeof category === 'string' && category.trim() !== ''
  )
}

const sortCategories = (categories, categoryOrder = []) => {
  const uniqueCategories = Array.from(new Set(categories))

  if (!Array.isArray(categoryOrder) || categoryOrder.length === 0) {
    const hasLimited = uniqueCategories.includes('Limited')
    const sortedCategories = uniqueCategories
      .filter((category) => category !== 'Limited')
      .sort((a, b) => a.localeCompare(b, 'ru'))

    return hasLimited ? ['Limited', ...sortedCategories] : sortedCategories
  }

  const categoryOrderMap = new Map(
    categoryOrder.map((category, index) => [category, index])
  )

  const orderedCategories = uniqueCategories
    .filter((category) => categoryOrderMap.has(category))
    .sort((a, b) => categoryOrderMap.get(a) - categoryOrderMap.get(b))

  const unorderedCategories = uniqueCategories
    .filter((category) => !categoryOrderMap.has(category))
    .sort((a, b) => a.localeCompare(b, 'ru'))

  return [...orderedCategories, ...unorderedCategories]
}

const BattleCreate = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { availableCases, categoryOrder, loadBattles, createBattle, isCreating, isLoading, error } = useBattleStore()
  const { user } = useAppStore()
  const { playSound } = useAudioManager({
    hover: 'hover',
    pick: 'pick',
    select: 'select'
  })
  
  // Состояния для выбора параметров
  const [selectedCase, setSelectedCase] = useState(null)
  const [countPlayers, setCountPlayers] = useState(2) // По умолчанию 2 игрока
  const [countCase, setCountCase] = useState(1) // По умолчанию 1 кейс
  const [battleType, setBattleType] = useState('more') // Режим победителя (more/less/random)
  const [showTypeHelp, setShowTypeHelp] = useState(false)
  
  // Состояние для активной категории (запоминаем выбор пользователя)
  const [activeCategory, setActiveCategory] = useState(() => {
    return localStorage.getItem('selectedBattleCaseCategory') || 'All'
  })
  const [showAllCategories, setShowAllCategories] = useState(false)
  const [shiningCaseId, setShiningCaseId] = useState(null)

  useEffect(() => {
    // Загружаем список баттлов и доступных кейсов
    if (availableCases.length === 0) {
      loadBattles()
    }
  }, [availableCases.length, loadBattles])
  
  // Сохраняем выбранную категорию в localStorage
  useEffect(() => {
    localStorage.setItem('selectedBattleCaseCategory', activeCategory)
  }, [activeCategory])

  const normalizedAvailableCases = useMemo(() => {
    return availableCases
      .map((caseItem) => ({
        ...caseItem,
        categories: getCaseCategories(caseItem)
      }))
      .filter((caseItem) => caseItem.categories.length > 0)
  }, [availableCases])

  const availableCategories = useMemo(() => {
    const allCategories = normalizedAvailableCases.flatMap((caseItem) => caseItem.categories)

    return ['All', ...sortCategories(allCategories, categoryOrder)]
  }, [normalizedAvailableCases, categoryOrder])

  const categorizedCases = useMemo(() => {
    const casesByCategory = new Map()

    normalizedAvailableCases.forEach((caseItem) => {
      caseItem.categories.forEach((category) => {
        if (!casesByCategory.has(category)) {
          casesByCategory.set(category, [])
        }

        casesByCategory.get(category).push(caseItem)
      })
    })

    return sortCategories(Array.from(casesByCategory.keys()), categoryOrder).map((category) => ({
      category,
      cases: [...casesByCategory.get(category)].sort(
        (a, b) => Number(a.price) - Number(b.price)
      )
    }))
  }, [normalizedAvailableCases, categoryOrder])

  const hasHiddenCategories = availableCategories.length > MAX_VISIBLE_CATEGORIES

  const visibleCategoryTabs = useMemo(() => {
    if (showAllCategories || !hasHiddenCategories) {
      return availableCategories
    }

    const defaultVisibleCategories = availableCategories.slice(0, MAX_VISIBLE_CATEGORIES)

    if (activeCategory && availableCategories.includes(activeCategory) && !defaultVisibleCategories.includes(activeCategory)) {
      return [...defaultVisibleCategories, activeCategory]
    }

    return defaultVisibleCategories
  }, [activeCategory, availableCategories, hasHiddenCategories, showAllCategories])

  const visibleSections = useMemo(() => {
    if (activeCategory === 'All') {
      return categorizedCases
    }

    return categorizedCases.filter(({ category }) => category === activeCategory)
  }, [categorizedCases, activeCategory])

  useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.includes(activeCategory)) {
      setActiveCategory('All')
    }
  }, [availableCategories, activeCategory])

  const getCategoryLabel = (category) => {
    if (category === 'All' || category === 'Limited') {
      return t(`cases.categories.${category}`)
    }

    return category
  }

  const handleCaseHover = () => {
    playSound('hover')
  }

  // Расчет итоговой стоимости создания баттла
  const totalCost = selectedCase ? selectedCase.price * countCase : 0
  const userBalance = Number(user?.balance || 0)
  const hasEnoughBalance = userBalance >= totalCost

  // Увеличение количества открытий кейса
  const incrementCountCase = () => {
    if (countCase < 5) {
      playSound('pick')
      setCountCase(countCase + 1)
    }
  }

  // Уменьшение количества открытий кейса
  const decrementCountCase = () => {
    if (countCase > 1) {
      playSound('pick')
      setCountCase(countCase - 1)
    }
  }

  const handleCreateBattle = async () => {
    if (!selectedCase) {
      toast.error(t('battle.selectCaseFirst'))
      return
    }

    // Проверка баланса на фронтенде
    if (user.balance < totalCost) {
      toast.error(t('cases.notEnoughCrystals', { price: totalCost }))
      navigate('/payment')
      return
    }

    // Отправляем цель в Яндекс.Метрику
    if (window.ym) {
      window.ym(104258304, 'reachGoal', 'create-case-battle')
    }

    try {
      const battleId = await createBattle({
        caseId: selectedCase.id,
        countPlayers,
        countCase,
        type: battleType
      })
      
      // toast.success(t('battle.battleStarted'))
      navigate(`/battles/${battleId}`)
    } catch (error) {
      // Обработка специфичных ошибок
      if (error.message === 'limit open battle') {
        toast.error(t('battle.limitReached'))
      } else if (error.message === 'not_enough_balance') {
        toast.error('Недостаточно кристаллов')
        navigate('/payment')
      } else if (error.message === 'error backend') {
        toast.error('Ошибка сервера, попробуйте позже')
      } else {
        toast.error(error.message || 'Ошибка создания баттла')
      }
    }
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-6 pb-24 lg:px-6 lg:py-8 lg:pb-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 lg:mb-8">
        <button
          onClick={() => navigate('/battles')}
          className="p-2 hover:bg-surface rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-3">
              <Swords className="w-6 h-6 text-accent-primary" />
              <h1 className="text-2xl font-bold text-white lg:text-3xl">
                {t('battle.createTitle', 'Создать баттл')}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-6 xl:grid-cols-[380px_minmax(0,1fr)] xl:gap-8">
        <div className="space-y-4 mb-6 lg:mb-0 lg:sticky lg:top-24 lg:self-start">
          {/* Параметры баттла */}
          <div className="space-y-4">
            {/* Выбор победителя */}
            <div className="bg-surface rounded-lg p-4 lg:p-5">
              <div className="flex items-center gap-2 mb-3">
                <Swords className="w-5 h-5 text-accent-primary" />
                <h3 className="text-white font-semibold">{t('battle.winnerSelectionTitle')}</h3>
                <button
                  type="button"
                  onClick={() => setShowTypeHelp(true)}
                  className="text-text-secondary hover:text-white transition-colors"
                  aria-label={t('battle.winnerHelp.ariaLabel')}
                  title={t('battle.winnerHelp.openLabel')}
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 lg:gap-3">
                {[
                  { key: 'more', label: t('battle.mode.more'), Icon: TrendingUp },
                  { key: 'less', label: t('battle.mode.less'), Icon: TrendingDown },
                  { key: 'random', label: t('battle.mode.random'), Icon: Dices }
                ].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => {
                      playSound('pick')
                      setBattleType(key)
                    }}
                    className={`
                      py-3 rounded-lg font-semibold transition-all flex items-center justify-center lg:min-h-[56px]
                      ${battleType === key
                        ? 'bg-accent-primary text-dark-primary'
                        : 'bg-dark-primary text-text-secondary hover:bg-dark-primary/70'
                      }
                    `}
                    aria-label={label}
                    title={label}
                  >
                    <Icon className="w-6 h-6" />
                  </button>
                ))}
              </div>
            </div>

            {/* Количество игроков */}
            <div className="bg-surface rounded-lg p-4 lg:p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-accent-primary" />
                <h3 className="text-white font-semibold">{t('battle.playersCount')}</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 lg:gap-3">
                {[2, 3, 4].map((num) => (
                  <button
                    key={num}
                    onClick={() => {
                      playSound('pick')
                      setCountPlayers(num)
                    }}
                    className={`
                      py-3 rounded-lg font-semibold transition-all lg:min-h-[56px]
                      ${countPlayers === num
                        ? 'bg-accent-primary text-dark-primary'
                        : 'bg-dark-primary text-text-secondary hover:bg-dark-primary/70'
                      }
                    `}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {selectedCase && (
              <div className="hidden lg:block bg-surface rounded-lg p-5 border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-accent-primary" />
                  <h3 className="text-white font-semibold">{t('battle.createTitle', 'Создать баттл')}</h3>
                </div>

                <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-dark-primary/60 border border-white/10">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gradient-to-br from-dark-primary to-surface flex items-center justify-center shrink-0">
                    <img
                      src={selectedCase.img}
                      alt={selectedCase.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white leading-tight line-clamp-2">
                      {selectedCase.name}
                    </p>
                    <div className="mt-2 flex items-center gap-1 text-accent-primary font-bold text-sm">
                      <Gem size={14} />
                      <span>{Number(selectedCase.price).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={decrementCountCase}
                      disabled={countCase <= 1}
                      className={`w-11 h-11 rounded-lg font-bold text-xl transition-all ${
                        countCase <= 1
                          ? 'bg-dark-primary text-text-secondary/50 cursor-not-allowed'
                          : 'bg-dark-primary text-white hover:bg-dark-primary/80 active:scale-95'
                      }`}
                      aria-label="Уменьшить количество открытий"
                    >
                      −
                    </button>
                    <div className="flex items-center justify-center min-w-[120px] h-11 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary/50 rounded-lg">
                      <span className="text-xl font-bold text-accent-primary">{countCase}</span>
                    </div>
                    <button
                      onClick={incrementCountCase}
                      disabled={countCase >= 5}
                      className={`w-11 h-11 rounded-lg font-bold text-xl transition-all ${
                        countCase >= 5
                          ? 'bg-dark-primary text-text-secondary/50 cursor-not-allowed'
                          : 'bg-dark-primary text-white hover:bg-dark-primary/80 active:scale-95'
                      }`}
                      aria-label="Увеличить количество открытий"
                    >
                      +
                    </button>
                  </div>
                </div>

                {hasEnoughBalance ? (
                  <AnimatedButton
                    onClick={handleCreateBattle}
                    disabled={isCreating}
                    soundFeedback={!isCreating}
                  >
                    {isCreating ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t('battle.creating')}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        {t('battle.createFor')}
                        <Gem className="w-5 h-5" />
                        {totalCost.toLocaleString()}
                      </span>
                    )}
                  </AnimatedButton>
                ) : (
                  <button
                    onClick={handleCreateBattle}
                    className="w-full py-4 rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-500/30"
                  >
                    {t('cases.topUpOn', { amount: Number(totalCost - userBalance).toLocaleString() })}
                    <Gem size={20} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="min-w-0">
          {/* Выбор кейса */}
          {/* <div className="bg-surface rounded-lg p-4 mb-4 lg:p-5 lg:mb-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Swords className="w-5 h-5 text-accent-primary" />
                <h3 className="text-white font-semibold">{t('battle.selectCase')}</h3>
              </div>
            </div>
          </div> */}

          {/* Табы категорий */}
          {!isLoading && !error && availableCategories.length > 0 && (
            <div className="mb-6">
              <div className="flex gap-2 flex-wrap justify-center lg:justify-start">
                {visibleCategoryTabs.map((category) => {
                  const isActive = activeCategory === category
                  const isLimitedTab = category === 'Limited'
                  
                  return (
                    <button
                      key={category}
                      onClick={() => {
                        playSound('pick')
                        setActiveCategory(category)
                      }}
                      className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? 'bg-accent-primary text-dark-primary'
                          : isLimitedTab
                            ? 'bg-gradient-to-r from-emerald-400/20 via-cyan-400/15 to-violet-500/20 text-emerald-200 '
                            : 'bg-surface text-text-secondary hover:bg-surface/80'
                      }`}
                    >
                      {getCategoryLabel(category)}
                    </button>
                  )
                })}

                {hasHiddenCategories && (
                  <button
                    onClick={() => {
                      playSound('pick')
                      setShowAllCategories((previousValue) => !previousValue)
                    }}
                    className="flex items-center justify-center rounded-lg border border-white/10 bg-surface px-4 py-2 text-text-secondary transition-colors hover:bg-surface/80 hover:text-white"
                  >
                    {showAllCategories ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                )}
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="animate-spin text-accent-primary" size={48} />
            </div>
          ) : error ? (
            <div className="text-center py-12 mb-6 bg-surface rounded-lg border border-white/10">
              <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {t('common.error')}
              </h3>
              <p className="text-text-secondary mb-4">
                {error}
              </p>
              <button 
                onClick={() => loadBattles()}
                className="bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition-all"
              >
                {t('common.retry')}
              </button>
            </div>
          ) : visibleSections.length > 0 ? (
            <div className="space-y-8 mb-6">
              {visibleSections.map(({ category, cases: categoryCases }) => (
                <section key={category}>
                  <h2 className="mt-12 mb-6 text-center text-3xl font-black tracking-[0.04em] text-accent-primary lg:mb-8 lg:text-5xl">
                    {getCategoryLabel(category)}
                  </h2>

                  <div className="flex flex-wrap justify-center gap-3 lg:justify-start">
                    {categoryCases.map((caseItem) => {
                      const isSelected = selectedCase?.id === caseItem.id
                      const itemTotalCost = Number(caseItem.price) * countCase
                      const canAfford = userBalance >= itemTotalCost

                      return (
                        <button
                          key={caseItem.id}
                          onClick={() => {
                            if (canAfford) {
                              playSound('select')
                              setSelectedCase(caseItem)
                            }
                          }}
                          onMouseEnter={() => {
                            handleCaseHover()
                            setShiningCaseId(caseItem.id)
                          }}
                          onMouseLeave={() => {
                            setShiningCaseId((currentCaseId) => currentCaseId === caseItem.id ? null : currentCaseId)
                          }}
                          className={`group relative w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-2.25rem)/4)] 2xl:w-[calc((100%-3rem)/5)] bg-surface rounded-lg border overflow-hidden cursor-pointer transition-colors duration-300 ease-out text-left ${
                            isSelected
                              ? 'border-accent-primary shadow-[0_0_0_1px_rgba(78,255,198,0.25)]'
                              : 'border-white border-opacity-10 hover:border-accent-primary/50'
                          }`}
                        >
                          <div className="relative aspect-square bg-gradient-to-br from-dark-primary to-surface flex items-center justify-center overflow-hidden">
                            {shiningCaseId === caseItem.id && (
                              <motion.div
                                key={caseItem.id}
                                className="pointer-events-none absolute inset-y-[-10%] left-[-45%] z-10 w-[60%] rotate-[18deg] mix-blend-screen"
                                style={{
                                  background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.02) 25%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.02) 75%, rgba(255,255,255,0) 100%)',
                                  boxShadow: '0 0 16px rgba(255, 255, 255, 0.08)'
                                }}
                                initial={{ x: '-60%', opacity: 1 }}
                                animate={{ x: '320%', opacity: [1, 1, 0] }}
                                transition={{ duration: 0.3, ease: 'linear' }}
                                onAnimationComplete={() => {
                                  setShiningCaseId((currentCaseId) => currentCaseId === caseItem.id ? null : currentCaseId)
                                }}
                              />
                            )}
                            <img
                              src={caseItem.img}
                              alt={caseItem.name}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-contain transition-transform duration-300 ease-out group-hover:scale-110"
                            />
                          </div>

                          <div className="p-3">
                            <h3 className="text-sm font-bold text-white mb-2 leading-tight">
                              {caseItem.name}
                            </h3>

                            <div className={`flex items-center gap-1 font-bold text-sm ${
                              canAfford ? 'text-accent-primary' : 'text-text-secondary'
                            }`}>
                              <Gem size={14} />
                              <span>{Number(caseItem.price).toLocaleString()}</span>
                            </div>
                          </div>

                          {!canAfford && (
                            <div
                              className="absolute inset-0 flex items-center justify-center bg-dark-primary/60 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate('/payment')
                              }}
                            >
                              <div className="text-center px-2">
                                <p className="text-sm text-white font-bold flex items-center justify-center gap-1">
                                  {t('cases.topUpOn', { amount: (itemTotalCost - userBalance).toLocaleString() })}
                                </p>
                              </div>
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 mb-6 bg-surface rounded-lg border border-white/10">
              <Package size={64} className="mx-auto text-text-secondary mb-4" />
              <h3 className="text-xl font-semibold text-text-secondary mb-2">
                {t('cases.noCases', 'Нет доступных кейсов')}
              </h3>
              <p className="text-text-secondary">
                {t('cases.noCasesDescription', 'В этой категории пока нет кейсов')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Button */}
      {selectedCase && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-dark-primary border-t border-white/10 z-40 lg:hidden">
          <div className="w-full max-w-[600px] mx-auto space-y-3">
            {/* Количество открытий кейса */}
            <div className="">
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={decrementCountCase}
                  disabled={countCase <= 1}
                  className={`w-10 h-10 rounded-lg font-bold text-xl transition-all ${
                    countCase <= 1
                      ? 'bg-surface text-text-secondary/50 cursor-not-allowed'
                      : 'bg-surface text-white hover:bg-surface/80 active:scale-95'
                  }`}
                  aria-label="Уменьшить количество открытий"
                >
                  −
                </button>
                <div className="flex items-center justify-center min-w-[100px] h-10 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary/50 rounded-lg">
                  <span className="text-xl font-bold text-accent-primary">{countCase}</span>
                </div>
                <button
                  onClick={incrementCountCase}
                  disabled={countCase >= 5}
                  className={`w-10 h-10 rounded-lg font-bold text-xl transition-all ${
                    countCase >= 5
                      ? 'bg-surface text-text-secondary/50 cursor-not-allowed'
                      : 'bg-surface text-white hover:bg-surface/80 active:scale-95'
                  }`}
                  aria-label="Увеличить количество открытий"
                >
                  +
                </button>
              </div>
            </div>

            {/* Кнопка создания баттла */}
            {hasEnoughBalance ? (
              <AnimatedButton
                onClick={handleCreateBattle}
                disabled={isCreating}
                soundFeedback={!isCreating}
              >
                {isCreating ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('battle.creating')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {t('battle.createFor')}
                    <Gem className="w-5 h-5" />
                    {totalCost.toLocaleString()}
                  </span>
                )}
              </AnimatedButton>
            ) : (
              <button
                onClick={handleCreateBattle}
                className="w-full py-4 rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-500/30"
              >
                {t('cases.topUpOn', { amount: Number(totalCost - userBalance).toLocaleString() })}
                <Gem size={20} />
              </button>
            )}
          </div>
        </div>
      )}
      <HelpModal
        isOpen={showTypeHelp}
        onClose={() => setShowTypeHelp(false)}
        title={t('battle.winnerHelp.title')}
        steps={[
          {
            title: (
              <span className="inline-flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-accent-primary" />
                {t('battle.mode.more')}
              </span>
            ),
            description: t('battle.winnerHelp.moreDesc')
          },
          {
            title: (
              <span className="inline-flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-accent-primary" />
                {t('battle.mode.less')}
              </span>
            ),
            description: t('battle.winnerHelp.lessDesc')
          },
          {
            title: (
              <span className="inline-flex items-center gap-2">
                <Dices className="w-4 h-4 text-accent-primary" />
                {t('battle.mode.random')}
              </span>
            ),
            description: t('battle.winnerHelp.randomDesc')
          }
        ]}
      />
    </div>
  )
}

export default BattleCreate
